import { BUY_BASE_COSTS, UPGRADE_BASE_COSTS, UPGRADE_CHAINS, BASE_COSTS, SCALING, WALL_HP_BASE, getUpgradeCost, FINGER } from '../config';
import { ChainUpgrade } from '../types';

export class EconomyManager {
  gold: number;

  // Elder bonus multipliers (synced from GameScene per frame)
  elderBuyCostMult: number = 1;
  elderChainCostMult: number = 1;

  // How many of each unit type are placed (for buy tab display) structure represents PAID units for scaling
  unitCounts: Record<string, number>;

  // Free units granted by prestige bonuses (does not affect cost scaling)
  freeUnitCounts: Record<string, number>;

  // Upgrade level for repeatable upgrades only
  upgradeLevels: Record<string, number>;

  // Chain upgrade progress per unit type (index into UPGRADE_CHAINS array)
  chainProgress: Record<string, number>;

  // Finger chain state (accumulated from finger chain upgrades)
  fingerChainMult: number = 1;
  fingerDamageFlat: number = 0;
  fingerClickGold: number = 0;
  fingerAoeRadius: number = 0;
  fingerAutoClickInterval: number = 0;

  constructor() {
    this.gold = 0;
    this.freeUnitCounts = {};
    this.unitCounts = {
      rock_thrower: 0,
      spear_thrower: 0,
      club_swinger: 0,
      archer: 0,
      sling_thrower: 0,
      longbow_archer: 0,
      builder: 0,
      crow_trainer: 0,
      crossbow: 0,
      firefly_nest: 0,
      trebuchet: 0,
      beehive_thrower: 0,
      druid: 0,
      lightning_wizard: 0,
      poison_arrows: 0,
      rock_rain: 0,
      gold_rush: 0,
      lightning_strike: 0,
    };
    this.upgradeLevels = {
      wall_upgrade: 0,
    };
    this.chainProgress = {
      finger: 0,
      rock_thrower: 0,
      spear_thrower: 0,
      club_swinger: 0,
      archer: 0,
      sling_thrower: 0,
      longbow_archer: 0,
      builder: 0,
      crow_trainer: 0,
      crossbow: 0,
      firefly_nest: 0,
      trebuchet: 0,
      beehive_thrower: 0,
      druid: 0,
      lightning_wizard: 0,
    };
  }

  addGold(amount: number): void {
    this.gold += amount;
  }

  canAfford(amount: number): boolean {
    return this.gold >= amount;
  }

  spendGold(amount: number): boolean {
    if (!this.canAfford(amount)) return false;
    this.gold -= amount;
    return true;
  }

  // Cost to buy a new unit instance (exponential: base × 1.18^count)
  getBuyCost(unitType: string): number {
    const baseCost = BUY_BASE_COSTS[unitType];
    if (baseCost !== undefined) {
      const count = this.unitCounts[unitType] || 0;
      return Math.round(baseCost * Math.pow(SCALING.unitCostScale, count) * this.elderBuyCostMult);
    }
    return Math.round((BASE_COSTS[unitType] || 50) * this.elderBuyCostMult);
  }

  // Cost to upgrade repeatable upgrades (exponential: base * 1.5^level, skills use 2.0^level)
  getUpgradeCost(unitType: string): number {
    const level = this.upgradeLevels[unitType] || 0;
    const baseCost = UPGRADE_BASE_COSTS[unitType] || BASE_COSTS[unitType] || 50;
    // Skills use steeper scaling (2.0^level)
    const skillTypes = ['poison_arrows', 'rock_rain', 'gold_rush', 'lightning_strike'];
    if (skillTypes.includes(unitType)) {
      return Math.round(baseCost * Math.pow(SCALING.skillUpgradeCostScale, level));
    }
    return getUpgradeCost(baseCost, level);
  }

  // ---- Chain upgrade methods ----

  getNextChainUpgrade(unitType: string): ChainUpgrade | null {
    const chain = UPGRADE_CHAINS[unitType];
    if (!chain) return null;
    const progress = this.chainProgress[unitType] || 0;
    if (progress >= chain.length) return null;
    return chain[progress];
  }

  getChainUpgradeCost(unitType: string): number {
    const upgrade = this.getNextChainUpgrade(unitType);
    if (!upgrade) return -1;
    return Math.round(upgrade.cost * this.elderChainCostMult);
  }

  isChainComplete(unitType: string): boolean {
    const chain = UPGRADE_CHAINS[unitType];
    if (!chain) return true;
    return (this.chainProgress[unitType] || 0) >= chain.length;
  }

  getChainProgress(unitType: string): number {
    return this.chainProgress[unitType] || 0;
  }

  getChainLength(unitType: string): number {
    return UPGRADE_CHAINS[unitType]?.length || 0;
  }

  purchaseChainUpgrade(unitType: string): ChainUpgrade | null {
    const upgrade = this.getNextChainUpgrade(unitType);
    if (!upgrade) return null;
    const cost = Math.round(upgrade.cost * this.elderChainCostMult);
    if (!this.spendGold(cost)) return null;
    this.chainProgress[unitType] = (this.chainProgress[unitType] || 0) + 1;
    return upgrade;
  }

  // Total units owned (excluding skills)
  getTotalUnitCount(): number {
    const skillKeys = ['poison_arrows', 'rock_rain', 'gold_rush', 'lightning_strike'];
    let total = 0;
    // Count paid units
    for (const [key, count] of Object.entries(this.unitCounts)) {
      if (!skillKeys.includes(key)) {
        total += count;
      }
    }
    // Count free units
    for (const [key, count] of Object.entries(this.freeUnitCounts)) {
      if (!skillKeys.includes(key)) {
        total += count;
      }
    }
    return total;
  }

  /** Total cost to buy `quantity` units starting from current count */
  getBulkBuyCost(unitType: string, quantity: number): number {
    const baseCost = BUY_BASE_COSTS[unitType];
    if (baseCost === undefined) return Math.round((BASE_COSTS[unitType] || 50) * quantity * this.elderBuyCostMult);
    const count = this.unitCounts[unitType] || 0;
    let total = 0;
    for (let i = 0; i < quantity; i++) {
      total += Math.round(baseCost * Math.pow(SCALING.unitCostScale, count + i) * this.elderBuyCostMult);
    }
    return total;
  }

  /** Max units of this type affordable with current gold */
  getMaxAffordable(unitType: string): number {
    const baseCost = BUY_BASE_COSTS[unitType];
    if (baseCost === undefined) {
      const flat = Math.round((BASE_COSTS[unitType] || 50) * this.elderBuyCostMult);
      return Math.floor(this.gold / flat);
    }
    const count = this.unitCounts[unitType] || 0;
    let remaining = this.gold;
    let n = 0;
    while (true) {
      const cost = Math.round(baseCost * Math.pow(SCALING.unitCostScale, count + n) * this.elderBuyCostMult);
      if (cost > remaining) break;
      remaining -= cost;
      n++;
    }
    return n;
  }

  getUnitCount(unitType: string): number {
    return (this.unitCounts[unitType] || 0) + (this.freeUnitCounts[unitType] || 0);
  }

  addFreeUnit(unitType: string): void {
    this.freeUnitCounts[unitType] = (this.freeUnitCounts[unitType] || 0) + 1;
  }

  getUpgradeLevel(unitType: string): number {
    return this.upgradeLevels[unitType] || 0;
  }

  // Buy a new unit instance
  purchaseBuy(unitType: string): boolean {
    const cost = this.getBuyCost(unitType);
    if (!this.spendGold(cost)) return false;
    this.unitCounts[unitType] = (this.unitCounts[unitType] || 0) + 1;
    return true;
  }

  // Upgrade repeatable upgrades
  purchaseUpgrade(unitType: string): boolean {
    const cost = this.getUpgradeCost(unitType);
    if (!this.spendGold(cost)) return false;
    this.upgradeLevels[unitType] = (this.upgradeLevels[unitType] || 0) + 1;

    return true;
  }

  // Legacy compatibility
  getCost(unitType: string): number {
    return this.getUpgradeCost(unitType);
  }

  getLevel(unitType: string): number {
    return this.upgradeLevels[unitType] || 0;
  }

  // ---- Computed multipliers ----

  getWallMaxHP(): number {
    const level = this.upgradeLevels['wall_upgrade'] || 0;
    return Math.round(WALL_HP_BASE * Math.pow(SCALING.wallHpGrowth, level));
  }

  getClickDamage(): number {
    return FINGER.baseClickDamage;
  }

  reset(): void {
    this.gold = 0;
    this.freeUnitCounts = {};
    for (const key of Object.keys(this.unitCounts)) {
      this.unitCounts[key] = 0;
    }
    for (const key of Object.keys(this.upgradeLevels)) {
      this.upgradeLevels[key] = 0;
    }
    for (const key of Object.keys(this.chainProgress)) {
      this.chainProgress[key] = 0;
    }
    this.fingerChainMult = 1;
    this.fingerDamageFlat = 0;
    this.fingerClickGold = 0;
    this.fingerAoeRadius = 0;
    this.fingerAutoClickInterval = 0;
  }

  // ---- Serialization (for save/load) ----

  getState(): {
    gold: number;
    unitCounts: Record<string, number>;
    freeUnitCounts: Record<string, number>;
    upgradeLevels: Record<string, number>;
    chainProgress: Record<string, number>;
    fingerChainMult: number;
    fingerDamageFlat: number;
    fingerClickGold: number;
    fingerAoeRadius: number;
    fingerAutoClickInterval: number;
  } {
    return {
      gold: this.gold,
      unitCounts: { ...this.unitCounts },
      freeUnitCounts: { ...this.freeUnitCounts },
      upgradeLevels: { ...this.upgradeLevels },
      chainProgress: { ...this.chainProgress },
      fingerChainMult: this.fingerChainMult,
      fingerDamageFlat: this.fingerDamageFlat,
      fingerClickGold: this.fingerClickGold,
      fingerAoeRadius: this.fingerAoeRadius,
      fingerAutoClickInterval: this.fingerAutoClickInterval,
    };
  }

  loadState(state: Partial<ReturnType<EconomyManager['getState']>>): void {
    if (state.gold !== undefined) this.gold = state.gold;
    if (state.unitCounts) Object.assign(this.unitCounts, state.unitCounts);
    if (state.freeUnitCounts) Object.assign(this.freeUnitCounts, state.freeUnitCounts);
    if (state.upgradeLevels) Object.assign(this.upgradeLevels, state.upgradeLevels);
    if (state.chainProgress) Object.assign(this.chainProgress, state.chainProgress);
    this.replayFingerChain();
  }

  private replayFingerChain(): void {
    this.fingerChainMult = 1;
    this.fingerDamageFlat = 0;
    this.fingerClickGold = 0;
    this.fingerAoeRadius = 0;
    this.fingerAutoClickInterval = 0;
    const chain = UPGRADE_CHAINS['finger'];
    if (!chain) return;
    const progress = this.chainProgress['finger'] || 0;
    for (let i = 0; i < progress; i++) {
      const e = chain[i].effect;
      if (e.fingerDamageFlat) this.fingerDamageFlat += e.fingerDamageFlat;
      if (e.fingerDamageMult) this.fingerChainMult *= (1 + e.fingerDamageMult);
      if (e.fingerClickGold) this.fingerClickGold += e.fingerClickGold;
      if (e.fingerAoeRadius) this.fingerAoeRadius = e.fingerAoeRadius;
      if (e.fingerAutoClick) this.fingerAutoClickInterval = e.fingerAutoClick;
    }
  }
}
